import React, { useEffect, useState } from 'react';
import styled from "styled-components"
import { Asset, Pool } from '@archetypes'
import { Button, Layout, LazyBoi, Tabbed, Panel, GateKeeper, Amount, Grid, Modal } from "@components";
import { ReactComponent as Info } from "@assets/Icon_Info_Large.svg"
import { ReactComponent as Refresh } from '@assets/Icon_Refresh.svg';
import appController from '@app/App.Controller'
import { parsePoolWeight, fromWei, calculateAPY, formatTimestamp } from "@util/helpers"
import useModal from "../../hooks/useModal"


const AssetItem = styled(
    ({ className, emp, expired }) => {

        const { wallet } = appController

        const { open, openModal, closeModal } = useModal()

        const [balance, setBalance] = useState("0")

        useEffect(() => {

            const sub = wallet.state.subscribe(`balances.${emp?.token1?.symbol}`, ({ balance }) => {
                setBalance(`${balance}`)
            }, true)

            return () => {
                sub.unsubscribe()
            }

        }, []) // eslint-disable-line

        return (
            <>
                <Panel className={className} fadeIn>
                    <Info onClick={openModal} className="icon-info" />
                    <div className="brand">
                        <Asset.Icon.SingleToken
                            token={[emp?.token1?.symbol]}
                            text={emp?.token1?.symbol}
                        />
                        <h4>
                            {emp?.token1?.name}
                        </h4>
                        <p>
                            Expires {formatTimestamp(emp?.expirationTimestamp)}
                        </p>
                    </div>
                    <Panel.Divider small light noMargin={true} />
                    <div className="stat" >
                        <div>
                            <h4>Min. PERL required</h4>
                            <p>{Number(fromWei(emp?.minCollateral)).toLocaleString()}</p>
                        </div>
                        {/* <div>
                            <h4>APY of Liquidity Pool</h4>
                            <p>{(calculateAPY(emp?.token1?.symbol)).toLocaleString()}%</p>
                        </div> */}
                    </div>
                    <Panel.Divider small light noMargin={true} />
                    <div className="action">
                        {((emp?.myPosition && emp?.myPosition.tokensOutstanding !== "0") || (expired && balance !== "0"))
                            ?
                            <div className="info">
                                {(expired && balance !== "0")
                                    ?
                                    (<div>
                                        <h4>Total Assets to Redeem</h4>
                                        <LazyBoi value={balance} fromWei suffix={emp?.token1?.symbol} />
                                    </div>)
                                    :
                                    (<div>
                                        <h4>Tokens minted</h4>
                                        <LazyBoi value={emp?.myPosition?.tokensOutstanding} fromWei />
                                    </div>)
                                }

                                <div>
                                    <Button style={{ height: "100%" }} to={`/mint/${emp?.address}`} small >
                                        {!expired ? "Manage" : "Redeem"}
                                    </Button>
                                </div>
                            </div>
                            : <Button.Full style={{ height: "100%", width: "100%" }} to={`/mint/${emp?.address}`}>Mint</Button.Full>
                        }
                    </div>
                </Panel>
                {open ? (
                    <Modal.AssetInfo
                        close={closeModal}
                        emp={emp}
                    />
                ) : null}
            </>
        )
    })`
        display: flex;
        flex-direction: column;

        >.icon-info {
            width: 24px;
            height: 24px;
            fill: var(--panel--background--color, grey);
            cursor: pointer;
            align-self: flex-end;
        }

        >.brand {
            text-align: center;

            h4 {
                line-height: 24px;
                font-size: 18px;
                font-weight: 600;
                color: var(--color-white); 
                margin-top: 1rem;
                margin-bottom: 0px;
            }

            p {
                margin-top: 0.3rem;
                font-size: 12px;
                line-height: 18px;
                margin-bottom: 2rem;
                font-weight: 600;
                color: var(--color-label, lightblue);
            }
        }
        >.stat {
            display: flex;
            padding-top: 1rem;
            padding-bottom: 1rem;
            div {
                flex: 1;
                font-weight: 600;
                h4 {
                    font-size: 14px;
                    color: var(--color-label, lightblue);
                    margin: 0px;
                    line-height: 18px;
                }
                p {
                    font-size: 16px;
                    color: var(--color-white);
                    margin: 0px;
                    line-height: 30px;

                }
            }
        }
        >.action {
            margin-top: 3rem; 
            font-size: 16px;
            font-weight: 600;

            >.info {
                display: flex;
                flex-direction: row;
                font-size: 14px;
                h4 {
                    font-size: 14px;
                    color: var(--color-label, lightblue);
                    margin: 0px;
                    line-height: 16px;
                    margin-bottom: 3px;
                }
                div {
                    flex: 1; 
                    font-weight: 600;
                    :last-child {
                        text-align: right;
                        
                    }
                }
            }

        }

    `

const AllAssets = () => {

    const { synthetics } = appController
    const [emps, setEmps] = useState({})

    useEffect(() => {
        const sub = synthetics.state.subscribe(`emps`, emps => setEmps({ ...emps }), true)
        return () => sub.unsubscribe()
    }, []) // eslint-disable-line

    const expiredEMPs = Object.keys(emps).filter(emp => Math.floor(new Date().valueOf() / 1000) > Number(emps[emp].expirationTimestamp)).map(empAddress => emps[empAddress])
    const activeEMPs = Object.keys(emps).filter(emp => Number(emps[emp].expirationTimestamp) >= Math.floor(new Date().valueOf() / 1000)).map(empAddress => emps[empAddress])

    return (
        <GateKeeper
            assertions={[
                {
                    assert: () => Object.values(emps).length > 0,
                    onFail: () => <Panel fadeIn><Refresh className='rotate' />Fetching Synthetic Assets</Panel>,
                }
            ]}
            dependencies={[emps]}
        >
            <Tabbed>
                <Tabbed.Child title={`Available to Mint`}>
                    <Grid cols={3}>
                        {activeEMPs.map((item, index) => (<AssetItem key={`active-emp-${index}`} emp={item} expired={false} />))}
                    </Grid>
                </Tabbed.Child>
                <Tabbed.Child title={`Expired Assets`}>
                    <Grid cols={3}>
                        {expiredEMPs.map((item, index) => (<AssetItem key={`expired-emp-${index}`} emp={item} expired={true} />))}
                    </Grid>
                </Tabbed.Child>
            </Tabbed>
        </GateKeeper>
    )
}


export default AllAssets